<template>
  <div class="space-y-8">
    <CreateRunnerPageHeading />

    <form class="space-y-8" @submit.prevent="onSubmit">
      <fieldset class="max-w-xl">
        <legend class="text-sm/6 font-medium text-gray-900 dark:text-white">Platform</legend>
        <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label
            v-for="option in platformOptions"
            :key="option"
            class="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 has-checked:border-indigo-600 has-checked:ring-1 has-checked:ring-indigo-600 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-white/5 dark:has-checked:border-indigo-500 dark:has-checked:ring-indigo-500"
          >
            <input
              :id="`platform-${option.toLowerCase()}`"
              v-model="platform"
              type="radio"
              name="platform"
              :value="option"
              class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 focus-visible:outline-none dark:border-white/10 dark:bg-white/5"
            />
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ PLATFORM_LABELS[option] }}
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset v-if="providerOptions.length > 1" class="max-w-xl" data-testid="provider-choice">
        <legend class="text-sm/6 font-medium text-gray-900 dark:text-white">Cloud provider</legend>
        <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label
            v-for="option in providerOptions"
            :key="option"
            class="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 has-checked:border-indigo-600 has-checked:ring-1 has-checked:ring-indigo-600 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-white/5 dark:has-checked:border-indigo-500 dark:has-checked:ring-indigo-500"
          >
            <input
              :id="`provider-${option}`"
              v-model="provider"
              type="radio"
              name="provider"
              :value="option"
              class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 focus-visible:outline-none dark:border-white/10 dark:bg-white/5"
            />
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ PROVIDER_TEXTS[option].name }}
            </span>
          </label>
        </div>
        <p
          v-if="provider === 'digitalocean'"
          class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          data-testid="digitalocean-costs"
        >
          {{ DIGITALOCEAN_COSTS_NOTE }}
        </p>
      </fieldset>

      <!-- Basic info -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-1 max-w-xl">
        <!-- Name -->
        <div>
          <label for="name" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Name</label
          >
          <div class="mt-2">
            <input
              type="text"
              name="name"
              id="name"
              v-model.trim="form.name"
              @input="touch('name')"
              :class="inputClass('name')"
              placeholder="e.g. swift-falcon"
              aria-describedby="name-desc"
              @blur="touch('name')"
              data-1p-ignore
            />
          </div>
          <p v-if="errors.name" class="mt-2 text-sm text-red-600 dark:text-red-400" id="name-error">
            {{ errors.name }}
          </p>
          <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="name-desc">
            Optional – we'll pick a name if you leave it blank.
          </p>
        </div>

        <!-- Product selection -->
        <div class="sm:col-span-1">
          <ProductSelect
            v-model="form.productId"
            :products="providerProducts"
            :loading="productsLoading"
            :select-class="selectClass('productId')"
            :aria-invalid="touched.productId && !!(errors.productId ?? '')"
            :error="errors.productId"
            :products-error="productsError"
          >
            <template #helper>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Choose a product to see its specifications and available locations.
              </p>
            </template>
          </ProductSelect>

          <!-- Selected product specs preview -->
          <div
            v-if="selectedSpecs"
            class="mt-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-white/10"
          >
            <dl class="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Chip
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.chip }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  vCPU
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.vcpu }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  RAM
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.ram }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Disk
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.disk }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <!-- Location (full width; price shown in option labels) -->
        <div>
          <label for="location" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Location</label
          >
          <div class="mt-2 grid grid-cols-1">
            <select
              id="location"
              name="location"
              v-model="form.location"
              :class="selectClass('location')"
              :aria-invalid="touched.location && !!(errors.location ?? '')"
              aria-describedby="location-feedback"
              :title="selectedLocationPriceTitle"
              :disabled="productsLoading || !selectedProduct || locationOptions.length === 0"
            >
              <option disabled value="">
                {{
                  !selectedProduct
                    ? 'Select a product first…'
                    : !hasAvailableLocations
                      ? 'No locations available'
                      : 'Select a location…'
                }}
              </option>
              <option
                v-for="loc in locationOptions"
                :key="loc.code"
                :value="loc.code"
                :disabled="!loc.available"
              >
                {{ formatLocationOptionLabel(loc) }}
              </option>
            </select>
          </div>
          <div id="location-feedback" class="mt-2 text-sm" aria-live="polite">
            <p
              v-if="errors.location"
              class="text-red-600 dark:text-red-400"
              id="location-error"
            >
              {{ errors.location }}
            </p>
            <p
              v-else-if="selectedProduct && locationOptions.length === 0"
              class="text-gray-500 dark:text-gray-400"
            >
              This product is not available in any location.
            </p>
            <p
              v-else-if="selectedProduct && !hasAvailableLocations"
              class="text-gray-500 dark:text-gray-400"
            >
              No locations are currently available for this product.
            </p>
          </div>
        </div>

        <!-- Executor + Concurrency -->
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div v-if="!isGithub">
            <label for="executor" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Executor</label
            >
            <div class="mt-2 grid grid-cols-1">
              <select
                id="executor"
                name="executor"
                v-model="form.executor"
                :class="selectClass('executor')"
                :aria-invalid="touched.executor && !!(errors.executor ?? '')"
                aria-describedby="executor-error"
              >
                <option value="docker">docker</option>
                <option value="dind">docker-in-docker</option>
                <option value="shell">shell</option>
              </select>
            </div>
            <p
              v-if="errors.executor"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
              id="executor-error"
            >
              {{ errors.executor }}
            </p>
          </div>

          <div>
            <label
              for="concurrency"
              class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Concurrency</label
            >
            <div class="mt-2">
              <input
                type="number"
                min="1"
                :max="isGithub ? GITHUB_MAX_CONCURRENCY : undefined"
                step="1"
                name="concurrency"
                id="concurrency"
                v-model.number="form.concurrency"
                :class="inputClass('concurrency')"
                placeholder="1"
              />
            </div>
            <p
              v-if="fieldServerErrors.concurrency || errors.concurrency"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
              id="concurrency-error"
            >
              {{ fieldServerErrors.concurrency || errors.concurrency }}
            </p>
          </div>
        </div>
      </div>
      <!-- GitLab Host -->
      <div v-if="!isGithub" class="sm:col-span-1 max-w-xl">
        <label for="gitlabHost" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
          >GitLab Host</label
        >
        <div class="mt-2">
          <input
            type="url"
            name="gitlabHost"
            id="gitlabHost"
            v-model.trim="form.gitlabHost"
            :class="inputClass('gitlabHost')"
            placeholder="https://gitlab.example.com"
            autocomplete="url"
            aria-describedby="gitlabHost-desc"
          />
        </div>
        <p
          v-if="errors.gitlabHost"
          class="mt-2 text-sm text-red-600 dark:text-red-400"
          id="gitlabHost-error"
        >
          {{ errors.gitlabHost }}
        </p>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="gitlabHost-desc">
          Base URL of your GitLab instance.
        </p>
      </div>

      <fieldset v-else class="max-w-xl space-y-6" data-testid="github-settings">
        <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">GitHub</legend>
        <fieldset>
          <legend class="block text-sm/6 font-medium text-gray-900 dark:text-white">Scope</legend>
          <div class="mt-2 flex flex-wrap gap-x-6 gap-y-2">
            <label
              v-for="option in githubScopeOptions"
              :key="option.value"
              class="flex items-center gap-2 text-sm text-gray-900 dark:text-white"
            >
              <input
                :id="`githubScope-${option.value}`"
                v-model="github.scope"
                type="radio"
                name="githubScope"
                :value="option.value"
                class="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5"
              />
              {{ option.label }}
            </label>
          </div>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {{
              github.scope === 'repository'
                ? 'The runner takes jobs of one repository.'
                : "The runner takes jobs of the organization's repositories that its runner group allows."
            }}
          </p>
        </fieldset>

        <div>
          <label
            for="githubOwner"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
          >
            {{ github.scope === 'repository' ? 'Owner' : 'Organization' }}
          </label>
          <div class="mt-2">
            <input
              id="githubOwner"
              v-model="github.owner"
              type="text"
              name="githubOwner"
              :class="inputClass('githubOwner')"
              placeholder="acme"
              autocomplete="off"
              :aria-invalid="!!shownError('githubOwner')"
              aria-describedby="githubOwner-feedback"
              @blur="touch('githubOwner')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="shownError('githubOwner')"
            id="githubOwner-feedback"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
          >
            {{ shownError('githubOwner') }}
          </p>
          <p v-else id="githubOwner-feedback" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {{
              github.scope === 'repository'
                ? 'The user or organization that owns the repository.'
                : 'The GitHub organization name.'
            }}
          </p>
        </div>

        <div v-if="github.scope === 'repository'">
          <label
            for="githubRepository"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Repository</label
          >
          <div class="mt-2">
            <input
              id="githubRepository"
              v-model="github.repository"
              type="text"
              name="githubRepository"
              :class="inputClass('githubRepository')"
              placeholder="app"
              autocomplete="off"
              :aria-invalid="!!shownError('githubRepository')"
              aria-describedby="githubRepository-feedback"
              @blur="touch('githubRepository')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="shownError('githubRepository')"
            id="githubRepository-feedback"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
          >
            {{ shownError('githubRepository') }}
          </p>
        </div>

        <div v-else>
          <label
            for="githubRunnerGroup"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Runner group
            <span class="font-normal text-gray-500 dark:text-gray-400">(optional)</span></label
          >
          <div class="mt-2">
            <input
              id="githubRunnerGroup"
              v-model="github.runnerGroup"
              type="text"
              name="githubRunnerGroup"
              :class="inputClass('githubRunnerGroup')"
              placeholder="Default"
              autocomplete="off"
              :aria-invalid="!!shownError('githubRunnerGroup')"
              aria-describedby="githubRunnerGroup-feedback"
              @blur="touch('githubRunnerGroup')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="shownError('githubRunnerGroup')"
            id="githubRunnerGroup-feedback"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
          >
            {{ shownError('githubRunnerGroup') }}
          </p>
          <p
            v-else
            id="githubRunnerGroup-feedback"
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            Leave empty for the organization's default runner group.
          </p>
        </div>

        <div>
          <label
            for="githubToken"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
          >
            {{ GITHUB_TOKEN_LABEL }}
          </label>
          <div class="mt-2">
            <input
              id="githubToken"
              v-model="github.token"
              type="password"
              name="githubToken"
              :class="inputClass('githubToken')"
              placeholder="github_pat_…"
              autocomplete="off"
              :aria-invalid="!!shownError('githubToken')"
              aria-describedby="githubToken-feedback githubToken-desc"
              @blur="touch('githubToken')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="shownError('githubToken')"
            id="githubToken-feedback"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
          >
            {{ shownError('githubToken') }}
          </p>
          <p
            v-if="githubTokenCleared"
            class="mt-2 text-sm text-gray-700 dark:text-gray-300"
            data-testid="github-token-cleared"
          >
            The token was cleared from this page. Enter it again to retry.
          </p>
          <p id="githubToken-desc" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {{ githubTokenHelp }}
            <a
              :href="GITHUB_TOKEN_PAGE"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Create a fine-grained token on GitHub</a
            >.
          </p>
        </div>
      </fieldset>

      <!-- Auth and tokens -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-1 max-w-xl">
        <!-- Runner Token -->
        <div v-if="!isGithub">
          <label for="runnerToken" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >GitLab Runner Token</label
          >
          <div class="mt-2">
            <input
              type="password"
              name="runnerToken"
              id="runnerToken"
              v-model.trim="form.runnerToken"
              :class="inputClass('runnerToken')"
              placeholder="GitLab runner registration token"
              @blur="touch('runnerToken')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="errors.runnerToken"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="runnerToken-error"
          >
            {{ errors.runnerToken }}
          </p>
        </div>

        <!-- Cloud provider token; the field keeps its Hetzner id for every provider -->
        <div>
          <label
            for="hetznerAuthToken"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >{{ texts.tokenLabel }}</label
          >
          <div class="mt-2">
            <input
              type="password"
              name="hetznerAuthToken"
              id="hetznerAuthToken"
              v-model.trim="form.hetznerAuthToken"
              :class="inputClass('hetznerAuthToken')"
              :placeholder="texts.tokenPlaceholder"
              aria-describedby="hetznerAuthToken-desc"
              @blur="onHetznerTokenBlur"
              data-1p-ignore
            />
          </div>
          <p
            v-if="hetznerTokenError"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="hetznerAuthToken-error"
          >
            {{ hetznerTokenError }}
          </p>
          <p
            v-else-if="errors.hetznerAuthToken"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="hetznerAuthToken-error"
          >
            {{ errors.hetznerAuthToken }}
          </p>
          <p
            v-else
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
            id="hetznerAuthToken-desc"
          >
            {{ texts.tokenHelp }}
            <a
              v-if="provider === 'digitalocean'"
              :href="DIGITALOCEAN_TOKEN_PAGE"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Create a token on DigitalOcean</a
            >
          </p>
          <!-- SSH keys selection -->
          <div class="mt-6">
            <SshKeyChecklist
              v-model="selectedSshKeyIds"
              :keys="sshKeys"
              :loading="sshKeysLoading"
              :error="sshKeysError"
              :label="texts.sshKeysLabel"
              :empty-message="texts.sshKeysEmpty"
            />
          </div>

          <!-- Labels -->
          <div class="mt-6">
            <label
              for="labels"
              class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Labels</label
            >
            <div class="mt-2">
              <LabelsInput
                v-model="form.labels"
                :suggestions="existingLabels"
                placeholder="Add a label..."
                id="labels"
                name="labels"
                :error="!!shownError('labels')"
              />
              <p
                v-if="shownError('labels')"
                class="mt-2 text-sm text-red-600 dark:text-red-400"
                id="labels-error"
              >
                {{ shownError('labels') }}
              </p>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="labels-desc">
                Hit Enter to add dynamically. Labels already used on your other runners are
                suggested.
                <template v-if="provider === 'digitalocean'">{{
                  DIGITALOCEAN_LABELS_NOTE
                }}</template>
                <template v-else
                  >Labels will be attached to the Hetzner VM. You can use them to automatically
                  apply Hetzner firewalls.</template
                >
                <template v-if="isGithub">GitHub also registers them as runner labels.</template>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isGithub" class="max-w-xl">
        <CacheSelect v-model="cacheId" />
      </div>

      <fieldset class="max-w-xl" aria-describedby="resource-metrics-desc">
        <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">Metrics</legend>
        <p id="resource-metrics-desc" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          A lightweight collector on the VM measures CPU, RAM, swap and disk usage and, on GitLab
          runners, the running jobs every minute.
        </p>
        <div class="mt-4 space-y-6">
          <RunnerToggle
            id="monitoringEnabled"
            v-model="monitoringEnabled"
            label="Show in dashboard"
            description="Charts on the runner card."
            :disabled="!canChangeMonitoring"
          />
          <MetricsSelect v-model="metricsConnectionId" />
          <RunnerWatchdogFields
            v-model:enabled="watchdog.enabled"
            v-model:timeout-minutes="watchdog.timeoutMinutes"
            :available="watchdogIsAvailable"
            :error="watchdogServerError"
          />
        </div>
      </fieldset>

      <!-- Actions / Submit -->
      <div class="flex items-center justify-between gap-3 max-w-xl">
        <button
          type="button"
          @click="router.push({ name: 'Dashboard' })"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="submitting || planBlocked"
        >
          <span v-if="!submitting">Create</span>
          <span v-else>Creating…</span>
        </button>
        <PlanRequiredNotice v-if="planBlocked" :recheck="recheckPlan" />
        <p v-else-if="serverError" class="text-sm text-red-600 dark:text-red-400 gap-5">
          {{ serverError }}
        </p>
        <p v-if="successMessage" class="text-sm text-green-700 dark:text-green-400 gap-5">
          {{ successMessage }}
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive, ref, watch} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CreateRunnerPageHeading from '@/components/CreateRunnerPageHeading.vue';
import ProductSelect from '@/components/ProductSelect.vue';
import LabelsInput from '@/components/LabelsInput.vue';
import SshKeyChecklist from '@/components/SshKeyChecklist.vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import CacheSelect from '@/components/CacheSelect.vue';
import api from '@/lib/api';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';
import MetricsSelect from '@/components/MetricsSelect.vue';
import RunnerWatchdogFields from '@/components/RunnerWatchdogFields.vue';
import {listProducts, type Product} from '@/api/products';
import {
  type CreateRunnerRequest,
  listExistingRunnerLabels,
  runnerProblem,
  type RunnerPlatform,
} from '@/api/runners';
import type {SshKeyDto} from '@/api/hetzner';
import {monthlyNetPriceTitle} from '@/lib/format';
import { formatLocationOptionLabel, locationOptions as productLocations } from '@/lib/locations';
import {validateRunnerName} from '@/lib/validation';
import {
  validateWatchdogTimeout,
  WATCHDOG_DEFAULT_TIMEOUT,
  watchdogAvailable,
  watchdogCreateFields,
  type WatchdogForm,
} from '@/lib/watchdog';
import PlanRequiredNotice from '@/components/PlanRequiredNotice.vue';
import { isSubscriptionRequired } from '@/lib/subscription-required';
import { usePlanRequired } from '@/lib/entitlement';
import {
  GITHUB_MAX_CONCURRENCY,
  GITHUB_TOKEN_PAGE,
  PLATFORM_LABELS,
  validateGithubConcurrency,
  validateGithubLabels,
  validateGithubOwner,
  validateGithubRepository,
  validateGithubRunnerGroup,
  validateGithubToken,
} from '@/lib/platforms';
import { GITHUB_TOKEN_LABEL, githubTokenHint, githubTokenPermission } from '@/lib/mcp-handoffs';
import {
  CLOUD_PROVIDERS,
  type CloudProvider,
  DIGITALOCEAN_COSTS_NOTE,
  DIGITALOCEAN_LABELS_NOTE,
  DIGITALOCEAN_TOKEN_PAGE,
  listProviderSshKeys,
  offersDigitalOcean,
  PROVIDER_TEXTS,
  productsOf,
  providerTokenFields,
} from '@/lib/providers';

type CreateRunnerForm = Required<
  Pick<
    CreateRunnerRequest,
    | 'name'
    | 'productId'
    | 'gitlabHost'
    | 'runnerToken'
    | 'hetznerAuthToken'
    | 'concurrency'
    | 'location'
    | 'executor'
    | 'labels'
  >
>;

interface RequestError {
  message?: string;
  response?: { data?: { detail?: string; message?: string } };
}

type GithubField = 'githubOwner' | 'githubRepository' | 'githubRunnerGroup' | 'githubToken';
type FieldKey = keyof CreateRunnerForm | GithubField;

const router = useRouter();
const route = useRoute();

const platformOptions: RunnerPlatform[] = ['GITLAB', 'GITHUB'];
const platform = ref<RunnerPlatform>(
  String(route.query.platform ?? '').toLowerCase() === 'github' ? 'GITHUB' : 'GITLAB'
);
const isGithub = computed(() => platform.value === 'GITHUB');

const githubScopeOptions = [
  { value: 'repository', label: 'Repository' },
  { value: 'organization', label: 'Organization' },
] as const;

const github = reactive({
  scope: 'repository' as 'repository' | 'organization',
  owner: '',
  repository: '',
  runnerGroup: '',
  token: '',
});
const githubTokenCleared = ref(false);

const form = reactive<CreateRunnerForm>({
  name: '',
  productId: '',
  gitlabHost: 'https://gitlab.com/',
  runnerToken: '',
  hetznerAuthToken: '',
  concurrency: 1,
  location: '',
  executor: 'docker',
  labels: [],
});

const touched = reactive<Record<FieldKey, boolean>>({
  githubOwner: false,
  githubRepository: false,
  githubRunnerGroup: false,
  githubToken: false,
  name: false,
  productId: false,
  gitlabHost: false,
  runnerToken: false,
  hetznerAuthToken: false,
  concurrency: false,
  location: false,
  executor: false,
  labels: false,
});

const errors = reactive<Partial<Record<FieldKey, string>>>({});
const fieldServerErrors = reactive<Partial<Record<FieldKey, string>>>({});
const submitting = ref(false);
const submitAttempted = ref(false);
const serverError = ref('');
const { planRequired, refresh: refreshPlan } = usePlanRequired();
// Set by a refusal for a missing plan; only a successful reload of the plan clears it.
const refused = ref(false);
const planBlocked = computed(() => refused.value || planRequired.value);
async function recheckPlan() {
  if (await refreshPlan()) refused.value = false;
}
const successMessage = ref('');

// SSH keys fetched from Hetzner by token on blur
const sshKeys = ref<SshKeyDto[]>([]);
const sshKeysLoading = ref(false);
const sshKeysError = ref('');
// Server-provided error message for invalid Hetzner token; displayed on the token field
const hetznerTokenError = ref('');
const selectedSshKeyIds = ref<string[]>([]);
// The provider and token a key lookup is for; a lookup for another one is discarded.
function keySource(): string {
  return `${provider.value}:${form.hetznerAuthToken.trim()}`;
}

const cacheId = ref('');

const monitoringEnabled = ref(false);
const { canChange: canChangeMonitoring } = useMonitoringEntitlement();
let monitoringDefaultApplied = false;
// Default on once entitlement is known; applied only once so a later subscription refresh keeps an opt-out.
watch(
  canChangeMonitoring,
  (canChange) => {
    if (!canChange || monitoringDefaultApplied) return;
    monitoringDefaultApplied = true;
    monitoringEnabled.value = true;
  },
  { immediate: true }
);

const metricsConnectionId = ref('');

const watchdog = reactive<WatchdogForm>({ enabled: false, timeoutMinutes: WATCHDOG_DEFAULT_TIMEOUT });
const watchdogServerError = ref('');
const watchdogIsAvailable = computed(() =>
  watchdogAvailable(monitoringEnabled.value, metricsConnectionId.value)
);
watch(watchdogIsAvailable, (available) => {
  if (!available) watchdog.enabled = false;
});
watch(
  () => [watchdog.enabled, watchdog.timeoutMinutes, watchdogIsAvailable.value],
  () => (watchdogServerError.value = '')
);

const allowedExecutors = ['docker', 'dind', 'shell'] as const;

// Products state
const products = ref<Product[]>([]);
const productsLoading = ref(false);
const productsError = ref<string>('');
const provider = ref<CloudProvider>('hetzner');
const providerOptions = computed<CloudProvider[]>(() =>
  offersDigitalOcean(products.value) ? CLOUD_PROVIDERS : ['hetzner']
);
const texts = computed(() => PROVIDER_TEXTS[provider.value]);
const providerProducts = computed(() => productsOf(products.value, provider.value));
const selectedProduct = computed(() =>
  providerProducts.value.find((p) => p.productId === form.productId)
);

// Labels already used on existing runners, offered as suggestions in the labels field
const existingLabels = ref<string[]>([]);

const locationOptions = computed(() => productLocations(selectedProduct.value));

const availableLocationCodes = computed(() => {
  const set = new Set<string>();
  for (const option of locationOptions.value) {
    if (option.available) set.add(option.code);
  }
  return set;
});

const hasAvailableLocations = computed(() => availableLocationCodes.value.size > 0);

const selectedLocationPrice = computed(() => {
  if (!form.productId || !form.location) return null;
  return locationOptions.value.find((loc) => loc.code === form.location)?.monthlyPrice ?? null;
});

const selectedLocationPriceTitle = computed(() =>
  selectedLocationPrice.value
    ? monthlyNetPriceTitle(selectedLocationPrice.value, texts.value.priceSource)
    : undefined
);

// Reset location if it becomes invalid for chosen product
watch(
  () => form.productId,
  () => {
    // Reset location if it becomes invalid for the newly chosen product
    if (form.location && !availableLocationCodes.value.has(form.location)) {
      form.location = '';
    }
    // Pre-fill concurrency based on selected product: 2 x vCPUs
    const vcpus = selectedProduct.value?.cpus;
    if (typeof vcpus === 'number' && !Number.isNaN(vcpus) && vcpus > 0) {
      form.concurrency = Math.max(1, vcpus * 2);
      if (isGithub.value) form.concurrency = Math.min(form.concurrency, GITHUB_MAX_CONCURRENCY);
    }
  }
);

// Products, locations, the token and its SSH keys belong to one provider.
watch(provider, () => {
  form.productId = '';
  form.location = '';
  form.hetznerAuthToken = '';
  touched.hetznerAuthToken = false;
  sshKeys.value = [];
  sshKeysError.value = '';
  selectedSshKeyIds.value = [];
});

watch(platform, (value) => {
  if (value === 'GITHUB' && form.concurrency > GITHUB_MAX_CONCURRENCY) {
    form.concurrency = GITHUB_MAX_CONCURRENCY;
  }
  // A token for a hidden form must not stay in memory.
  if (value !== 'GITHUB') {
    github.token = '';
    githubTokenCleared.value = false;
    delete fieldServerErrors.githubToken;
  }
  if (submitAttempted.value) validate();
});

const githubTokenHelp = computed(() => {
  const organization = github.scope === 'organization';
  const scopeKnown =
    !validateGithubOwner(github.owner) &&
    (organization || !validateGithubRepository(github.repository));
  return scopeKnown
    ? githubTokenHint({
        owner: github.owner.trim(),
        repository: organization ? null : github.repository.trim(),
      })
    : `A fine-grained personal access token with ${githubTokenPermission(organization)}.`;
});

// Product specs to preview beneath selector
const selectedSpecs = computed(() => {
  const p = selectedProduct.value;
  if (!p) return null;
  return {
    chip: String(p.cpuArchitecture.value ?? ''),
    vcpu: String(p.cpus ?? ''),
    ram: String(p.memory ?? ''),
    disk: String(p.disk ?? ''),
  };
});

function validate(field?: FieldKey): boolean {
  const gitlab = !isGithub.value;
  const repositoryScope = github.scope === 'repository';
  if (!field || field === 'githubOwner') {
    errors.githubOwner = gitlab ? '' : validateGithubOwner(github.owner);
  }
  if (!field || field === 'githubRepository') {
    errors.githubRepository =
      gitlab || !repositoryScope ? '' : validateGithubRepository(github.repository);
  }
  if (!field || field === 'githubRunnerGroup') {
    errors.githubRunnerGroup =
      gitlab || repositoryScope ? '' : validateGithubRunnerGroup(github.runnerGroup);
  }
  if (!field || field === 'githubToken') {
    errors.githubToken = gitlab
      ? ''
      : github.token.trim()
        ? validateGithubToken(github.token)
        : 'GitHub token is required.';
  }
  if (!field || field === 'labels') {
    errors.labels = gitlab ? '' : validateGithubLabels(form.labels);
  }
  if (!field || field === 'name') {
    errors.name = form.name ? validateRunnerName(form.name) : '';
  }
  if (!field || field === 'productId') {
    errors.productId = form.productId ? '' : 'Product is required.';
  }
  if (!field || field === 'gitlabHost') {
    errors.gitlabHost = !gitlab || form.gitlabHost ? '' : 'GitLab Host is required.';
  }
  if (!field || field === 'runnerToken') {
    errors.runnerToken = !gitlab || form.runnerToken ? '' : 'Runner token is required.';
  }
  if (!field || field === 'hetznerAuthToken') {
    errors.hetznerAuthToken = form.hetznerAuthToken ? '' : texts.value.tokenRequired;
  }
  if (!field || field === 'concurrency') {
    errors.concurrency = !gitlab
      ? validateGithubConcurrency(form.concurrency)
      : !form.concurrency || form.concurrency < 1
        ? 'Concurrency must be at least 1.'
        : '';
  }
  if (!field || field === 'location') {
    const validLoc = availableLocationCodes.value.has(form.location);
    errors.location = form.location && validLoc ? '' : 'Location is required.';
  }
  if (!field || field === 'executor') {
    errors.executor =
      !gitlab || (form.executor && (allowedExecutors as readonly string[]).includes(form.executor))
        ? ''
        : 'Executor must be docker, dind, or shell.';
  }
  return !Object.values(errors).some((e) => e);
}

function touch(key: FieldKey) {
  touched[key] = true;
  validate(key);
}

function shownError(key: FieldKey): string {
  return fieldServerErrors[key] || (touched[key] ? (errors[key] ?? '') : '');
}

function inputClass(key: FieldKey) {
  const base =
    'block w-full rounded-md bg-white px-3 py-1.5 text-base outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 dark:bg-white/5';
  const normal = `${base} text-gray-900 outline-gray-300 focus:outline-indigo-600 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500`;
  const error = `${base} text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600 dark:text-red-400 dark:outline-red-500/50 dark:placeholder:text-red-400/70 dark:focus:outline-red-400`;
  // If the field is the Hetzner token, also consider backend token errors
  const extraError = key === 'hetznerAuthToken' ? hetznerTokenError.value : '';
  const showError = fieldServerErrors[key] || (touched[key] && ((errors[key] ?? '') || extraError));
  return showError ? error : normal;
}

function selectClass<K extends keyof CreateRunnerForm>(key: K) {
  const base =
    'w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base outline-1 -outline-offset-1 focus-visible:outline-2 focus-visible:-outline-offset-2 sm:text-sm/6 dark:bg-white/5';
  const normal = `${base} text-gray-900 outline-gray-300 focus-visible:outline-indigo-600 dark:text-white dark:outline-white/10 dark:*:bg-gray-800 dark:focus-visible:outline-indigo-500`;
  const error = `${base} text-red-900 outline-red-300 focus-visible:outline-red-600 dark:text-red-400 dark:outline-red-500/50 dark:focus-visible:outline-red-400`;
  const showError = touched[key] && (errors[key] ?? '');
  return showError ? error : normal;
}

// Fetch products on mount
onMounted(async () => {
  productsLoading.value = true;
  productsError.value = '';
  try {
    products.value = await listProducts();
  } catch (failure) {
    const e = failure as RequestError | null;
    productsError.value = e?.response?.data?.message || e?.message || 'Failed to load products';
  } finally {
    productsLoading.value = false;
  }
  // Label suggestions are a convenience; a failure must not block the form.
  try {
    existingLabels.value = await listExistingRunnerLabels();
  } catch {
    existingLabels.value = [];
  }
});

// When user leaves the Hetzner token field, fetch SSH keys from backend
async function onHetznerTokenBlur() {
  touched.hetznerAuthToken = true;
  validate('hetznerAuthToken');
  const token = form.hetznerAuthToken?.trim();
  if (!token) {
    sshKeys.value = [];
    sshKeysError.value = '';
    hetznerTokenError.value = '';
    selectedSshKeyIds.value = [];
    return;
  }
  const source = keySource();
  sshKeysLoading.value = true;
  sshKeysError.value = '';
  hetznerTokenError.value = '';
  try {
    const data = await listProviderSshKeys(provider.value, token);
    if (source !== keySource()) return;
    sshKeys.value = Array.isArray(data) ? data : [];
    // Keep only selections that still exist in the fetched list
    const validIds = new Set(sshKeys.value.map((k) => String(k.id)));
    selectedSshKeyIds.value = selectedSshKeyIds.value.filter((id) => validIds.has(id));
  } catch (failure) {
    if (source !== keySource()) return;
    const e = failure as RequestError | null;
    sshKeys.value = [];
    // Show invalid Hetzner token errors on the token field (not under SSH keys)
    // Prefer backend `detail`, then `message`, then generic error.
    hetznerTokenError.value =
      e?.response?.data?.detail ||
      e?.response?.data?.message ||
      e?.message ||
      `Failed to validate ${texts.value.name} token`;
    // Do not duplicate error under SSH keys section
    sshKeysError.value = '';
    selectedSshKeyIds.value = [];
  } finally {
    if (source === keySource()) sshKeysLoading.value = false;
  }
}

watch(
  () => form.name,
  (newValue) => {
    // Preserve spaces in names while removing unsupported characters.
    const invalidCharsRegex = /[^a-zA-Z0-9 -]/g;
    const sanitizedValue = newValue.replace(invalidCharsRegex, '');
    if (sanitizedValue !== newValue) {
      form.name = sanitizedValue;
    }
  }
);
// A lookup still in flight belongs to the previous provider or token.
watch(keySource, () => {
  hetznerTokenError.value = '';
  sshKeysLoading.value = false;
});
watch(
  () => github.owner,
  () => {
    delete fieldServerErrors.githubOwner;
    delete fieldServerErrors.githubRepository;
  }
);
watch(
  () => github.repository,
  () => delete fieldServerErrors.githubRepository
);
watch(
  () => github.scope,
  () => delete fieldServerErrors.githubRepository
);
// Clearing the token after a request must not hide the refusal that came back for it.
watch(
  () => github.token,
  (token) => {
    if (!token) return;
    delete fieldServerErrors.githubToken;
    githubTokenCleared.value = false;
  }
);
watch(
  () => form.concurrency,
  () => delete fieldServerErrors.concurrency
);
watch(
  () => form.labels,
  () => delete fieldServerErrors.labels,
  { deep: true }
);
// After the first submit click, keep validation live as the user edits
watch(
  [form, github],
  () => {
    if (submitAttempted.value) {
      validate();
    }
  },
  { deep: true }
);

async function onSubmit() {
  if (planBlocked.value) return;
  serverError.value = '';
  successMessage.value = '';
  submitAttempted.value = true;
  // Mark all as touched to show validation messages
  (Object.keys(touched) as FieldKey[]).forEach((k) => (touched[k] = true));

  if (!validate()) return;
  if (watchdog.enabled && validateWatchdogTimeout(watchdog.timeoutMinutes)) return;

  submitting.value = true;
  const sentToGithub = isGithub.value;
  for (const key of Object.keys(fieldServerErrors) as FieldKey[]) delete fieldServerErrors[key];
  watchdogServerError.value = '';
  try {
    const payload: CreateRunnerRequest = sentToGithub
      ? {
          name: form.name || undefined,
          productId: form.productId,
          platform: 'GITHUB',
          github: githubSettings(),
          ...providerTokenFields(provider.value, form.hetznerAuthToken),
          concurrency: Number(form.concurrency),
          location: form.location,
          sshKeyIds: selectedSshKeyIds.value,
          labels: form.labels,
        }
      : {
          name: form.name || undefined,
          productId: form.productId,
          gitlabHost: form.gitlabHost,
          runnerToken: form.runnerToken,
          ...providerTokenFields(provider.value, form.hetznerAuthToken),
          concurrency: Number(form.concurrency),
          location: form.location,
          executor: form.executor,
          sshKeyIds: selectedSshKeyIds.value,
          labels: form.labels,
        };
    if (sentToGithub) {
      github.token = '';
      // The cleared-token notice explains the empty field better than a required error.
      touched.githubToken = false;
    }
    if (canChangeMonitoring.value) {
      payload.monitoringEnabled = monitoringEnabled.value;
    }
    if (metricsConnectionId.value) payload.metricsConnectionId = metricsConnectionId.value;
    if (cacheId.value && !sentToGithub) payload.cacheId = cacheId.value;
    Object.assign(payload, watchdogCreateFields(watchdog, watchdogIsAvailable.value));

    const { data } = await api.post<{ runnerId: string; name?: string }>('runner', payload);
    const name = data.name ?? form.name;
    successMessage.value = name
      ? `Runner ${name} created (ID: ${data.runnerId}).`
      : `Runner created (ID: ${data.runnerId}).`;
    // Navigate back to dashboard after a short delay
    setTimeout(() => router.push({ name: 'Dashboard' }), 600);
  } catch (err) {
    if (isSubscriptionRequired(err)) {
      refused.value = true;
      void recheckPlan();
    }
    // Prefer backend-provided `detail` when available (e.g., invalid Hetzner token),
    // then fall back to `message`, else a generic error.
    const problem = runnerProblem(err, 'Failed to create runner. Please try again.');
    const field = sentToGithub ? githubErrorField(problem.code) : undefined;
    if (field) {
      fieldServerErrors[field] = problem.message;
      serverError.value = 'Check the highlighted field above.';
    } else if (problem.code === 'INVALID_WATCHDOG') {
      watchdogServerError.value = problem.message;
      serverError.value = 'Check the highlighted field above.';
    } else {
      serverError.value = problem.message;
    }
    if (sentToGithub) githubTokenCleared.value = true;
  } finally {
    submitting.value = false;
  }
}

function githubSettings(): NonNullable<CreateRunnerRequest['github']> {
  const owner = github.owner.trim();
  const token = github.token.trim();
  if (github.scope === 'repository') return { owner, repository: github.repository.trim(), token };
  const runnerGroup = github.runnerGroup.trim();
  return runnerGroup ? { owner, runnerGroup, token } : { owner, token };
}

function githubErrorField(code: string | undefined): FieldKey | undefined {
  switch (code) {
    case 'GITHUB_TOKEN_INVALID':
    case 'GITHUB_PERMISSION_DENIED':
      return 'githubToken';
    case 'GITHUB_NOT_FOUND':
      return github.scope === 'repository' ? 'githubRepository' : 'githubOwner';
    case 'INVALID_CONCURRENCY':
      return 'concurrency';
    case 'INVALID_LABELS':
      return 'labels';
    default:
      return undefined;
  }
}
</script>
